const STRAICO_COMPLETION_URL = 'https://api.straico.com/v1/prompt/completion';
const STRAICO_MODELS_URL = 'https://api.straico.com/v1/models';
const OPENROUTER_MODELS_URL = 'https://openrouter.ai/api/v1/models';
const OPENROUTER_COMPLETION_URL = 'https://openrouter.ai/api/v1/chat/completions';

export const fetchModels = async (apiKey, provider) => {
  try {
    if (provider === 'openrouter') {
      const res = await fetch(OPENROUTER_MODELS_URL, { 
        headers: { 
          'Authorization': `Bearer ${apiKey}`,
          'HTTP-Referer': 'https://creditsonar.ai',
          'X-Title': 'CreditSonar'
        } 
      });
      const json = await res.json();
      return json.data.map(m => ({
        model: m.id,
        name: m.name,
        cost: {
          input: (m.pricing.prompt * 1000000).toFixed(2),
          output: (m.pricing.completion * 1000000).toFixed(2)
        }
      }));
    }
    
    if (provider === 'straico') {
      const res = await fetch(STRAICO_MODELS_URL, { headers: { 'Authorization': `Bearer ${apiKey}` } });
      const json = await res.json();
      const models = json.data?.chat || json.data || [];
      return models.map(m => ({
        model: m.model,
        name: m.name,
        cost: m.pricing ? {
          input: (m.pricing.input * 1000).toFixed(2),
          output: (m.pricing.output * 1000).toFixed(2)
        } : null
      }));
    }

    if (provider === 'gemini') {
      return [
        { model: 'gemini-1.5-pro', name: 'Gemini 1.5 Pro', cost: { input: '3.50', output: '10.50' } },
        { model: 'gemini-1.5-flash', name: 'Gemini 1.5 Flash', cost: { input: '0.07', output: '0.30' } },
        { model: 'gemini-2.0-flash-exp', name: 'Gemini 2.0 Flash (Exp)', cost: { input: 'Free', output: 'Free' } }
      ];
    }

    if (provider === 'deepseek') {
      return [
        { model: 'deepseek-chat', name: 'DeepSeek-V3', cost: { input: '0.14', output: '0.28' } },
        { model: 'deepseek-reasoner', name: 'DeepSeek-R1 (Reasoning)', cost: { input: '0.55', output: '2.19' } }
      ];
    }

    if (provider === 'qwen') {
      return [
        { model: 'qwen-max', name: 'Qwen-Max (Turbo)', cost: { input: '2.80', output: '8.40' } },
        { model: 'qwen-plus', name: 'Qwen-Plus', cost: { input: '0.50', output: '1.50' } },
        { model: 'qwen-turbo', name: 'Qwen-Turbo', cost: { input: '0.10', output: '0.30' } }
      ];
    }

    const defaults = {
      openai: [
        { model: 'gpt-4o', name: 'GPT-4o', cost: { input: '5.00', output: '15.00' } },
        { model: 'gpt-4o-mini', name: 'GPT-4o Mini', cost: { input: '0.15', output: '0.60' } }
      ],
      anthropic: [
        { model: 'claude-3-5-sonnet-20240620', name: 'Claude 3.5 Sonnet', cost: { input: '3.00', output: '15.00' } },
        { model: 'claude-3-opus-20240229', name: 'Claude 3 Opus', cost: { input: '15.00', output: '75.00' } }
      ]
    };
    return defaults[provider] || [];
  } catch (err) {
    console.error('Model fetch error:', err);
    return [];
  }
};

export const callAPI = async (prompt, apiKey, provider, selectedModel) => {
  // Provider-specific routing
  if (provider === 'openrouter') {
    const res = await fetch(OPENROUTER_COMPLETION_URL, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ model: selectedModel, messages: [{ role: 'user', content: prompt }], temperature: 0.1 }),
    });
    const json = await res.json();
    if (json.error) throw new Error(json.error.message);
    return json.choices[0].message.content;
  }

  if (provider === 'gemini') {
    const res = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${selectedModel}:generateContent?key=${apiKey}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contents: [{ parts: [{ text: prompt }] }] }),
    });
    const json = await res.json();
    if (json.error) throw new Error(json.error.message);
    return json.candidates[0].content.parts[0].text;
  }

  if (provider === 'deepseek') {
    const res = await fetch('https://api.deepseek.com/chat/completions', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ model: selectedModel, messages: [{ role: 'user', content: prompt }], temperature: 0.1 }),
    });
    const json = await res.json();
    if (json.error) throw new Error(json.error.message);
    return json.choices[0].message.content;
  }

  if (provider === 'qwen') {
    const res = await fetch('https://dashscope.aliyuncs.com/compatible-mode/v1/chat/completions', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ model: selectedModel, messages: [{ role: 'user', content: prompt }], temperature: 0.1 }),
    });
    const json = await res.json();
    if (json.error) throw new Error(json.error.message);
    return json.choices[0].message.content;
  }

  // Fallback to Straico
  const res = await fetch(STRAICO_COMPLETION_URL, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ models: [selectedModel], message: prompt, temperature: 0.1 }),
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.message || "API Request Failed");
  const key = Object.keys(json.data.completions)[0];
  return json.data.completions[key].completion.choices[0].message.content;
};

export const fetchEntityResolution = async (name, location, apiKey, provider, model) => {
  const prompt = `Act as an institutional credit analyst. Resolve this entity: "${name}" ${location ? `located in ${location}` : ''}. Return a JSON object with a "candidates" array. Each candidate must have: id, legalName, jurisdiction, primarySource, functionalCurrency, confidence (0-100), and notes.`;
  const raw = await callAPI(prompt, apiKey, provider, model);
  try {
    const cleaned = raw.replace(/```json|```/g, '').trim();
    return JSON.parse(cleaned);
  } catch (e) {
    throw new Error("Failed to parse entity resolution response.");
  }
};

export const fetchCreditMemo = async (apiKey, provider, model, candidate, layout) => {
  const prompt = `Generate a ${layout === 'short' ? 'concise 5-section' : 'comprehensive 12-section'} credit memorandum in Markdown for ${candidate.legalName}. Include Financial Risk, OSINT Intelligence, and a Final Verdict. Use tables for financial data.`;
  return await callAPI(prompt, apiKey, provider, model);
};